import React, { useEffect, useMemo, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faDownload, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import GenericQrDocument from "./GenericQrDocument";
import { handleTokenAndPostRequest } from "../../utils/utils";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
  background: "#fff",
};

const labelStyle = {
  display: "block",
  fontWeight: 600,
  fontSize: 12,
  color: "#374151",
  marginBottom: 6,
};

function Field({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </div>
  );
}

const urlToDataUrl = async (url) => {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    try {
      return await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          canvas.getContext("2d").drawImage(image, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = reject;
        image.src = url;
      });
    } catch {
      return null;
    }
  }
};

export default function GenericQrTab({ isMobile, qrCodeUrl, slugLoading }) {
  const [agent, setAgent] = useState(null);
  const [agentLoading, setAgentLoading] = useState(true);

  const [headline, setHeadline] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [eyebrow, setEyebrow] = useState("");

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/property-qr-context`,
          { external_properties: [] }
        );
        if (cancelled) return;
        const a = res.data?.agent || {};
        const [logo, profile] = await Promise.all([
          urlToDataUrl(a.logo_url),
          urlToDataUrl(a.profile_image_url),
        ]);
        if (cancelled) return;
        setAgent({ ...a, logo_url: logo || "", profile_image_url: profile || "" });
      } catch (e) {
        console.error("Failed to load agent context", e);
        setAgent({});
      }
      setAgentLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildPdf = async () => {
    if (!qrCodeUrl) return;
    setPdfLoading(true);
    setPdfBlob(null);
    setPdfUrl(null);
    try {
      const qrDataUrl = await QRCode.toDataURL(qrCodeUrl, {
        errorCorrectionLevel: "M",
        margin: 1,
        scale: 8,
      });
      const blob = await pdf(
        <GenericQrDocument
          agent={agent || {}}
          qrDataUrl={qrDataUrl}
          headline={headline}
          subtitle={subtitle}
          eyebrow={eyebrow}
        />
      ).toBlob();
      setPdfBlob(blob);
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (err) {
      console.error("Generic QR PDF error", err);
      alert("Failed to generate PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "qr_flyer.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const copyLink = () => {
    if (!qrCodeUrl) return;
    navigator.clipboard.writeText(qrCodeUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const card = useMemo(
    () => ({
      background: "#fff",
      borderRadius: 12,
      padding: isMobile ? 16 : 24,
      boxShadow: "0 2px 8px #0001",
      marginBottom: 20,
    }),
    [isMobile]
  );

  return (
    <div>
      <div style={card}>
        <h3 style={{ fontWeight: 600, fontSize: 20, marginBottom: 6 }}>
          Generic QR Flyer
        </h3>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 0, lineHeight: 1.55 }}>
          Generate a print-ready A4 PDF with your QR code. Scans take people to
          your landing page where they can request a free property estimate.
          Customise the copy below — leave fields blank for sensible defaults.
        </p>
      </div>

      <div style={card}>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Your QR landing page link</label>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              readOnly
              value={slugLoading ? "Loading…" : (qrCodeUrl || "")}
              style={{ ...inputStyle, background: "#f9fafb", fontSize: 13 }}
            />
            <button
              onClick={copyLink}
              disabled={!qrCodeUrl}
              style={{
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: qrCodeUrl ? "pointer" : "not-allowed",
                opacity: qrCodeUrl ? 1 : 0.6,
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
              {copySuccess ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 14,
          }}
        >
          <Field
            label="Eyebrow"
            value={eyebrow}
            onChange={setEyebrow}
            placeholder="Free property estimate"
          />
          <Field
            label="Headline"
            value={headline}
            onChange={setHeadline}
            placeholder="What's your home really worth?"
          />
          <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
            <Field
              label="Subtitle"
              value={subtitle}
              onChange={setSubtitle}
              placeholder="Get a free, instant property report — no obligation, no sign-up."
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
          <button
            onClick={buildPdf}
            disabled={pdfLoading || agentLoading || !qrCodeUrl}
            style={{
              background: "#7c3aed",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: pdfLoading || agentLoading || !qrCodeUrl ? "not-allowed" : "pointer",
              opacity: pdfLoading || agentLoading || !qrCodeUrl ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {pdfLoading ? (
              <>
                <Spinner animation="border" size="sm" /> Generating PDF…
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faFilePdf} /> {pdfBlob ? "Regenerate PDF" : "Generate PDF"}
              </>
            )}
          </button>
          {pdfBlob && (
            <button
              onClick={downloadPdf}
              style={{
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <FontAwesomeIcon icon={faDownload} /> Download
            </button>
          )}
        </div>

        {pdfUrl && (
          <iframe
            title="QR flyer preview"
            src={pdfUrl}
            style={{
              width: "100%",
              height: isMobile ? 500 : 720,
              marginTop: 16,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              background: "#f9fafb",
            }}
          />
        )}
      </div>
    </div>
  );
}
